import { MdOutlineCancel } from "react-icons/md";
import { FaRegCheckCircle } from "react-icons/fa";
import { useMemo } from "react";
import { OrderImage } from "src/types";
import { useQuery } from "@tanstack/react-query";
import { orderImagesQueryOptions } from "src/api/mvte-query-options";
import { MdErrorOutline } from "react-icons/md";
import { useOrderInvoicing } from "../context/order-invoicing-state";

type ImagesById = Map<number | null, OrderImage[]>;
type ImagesGrouped = Record<string, ImagesById>;

export function OrderDocuments({
    orderNumber,
}: {
    orderNumber: string | null;
}) {
    const orderInvoicing = useOrderInvoicing();
    const orderImagesQuery = useQuery({
        ...orderImagesQueryOptions({ orderNumber: orderNumber || "" }),
        enabled: orderNumber !== null,
    });

    const groupedImages = useMemo(() => {
        if (!orderImagesQuery.data) return {};

        return orderImagesQuery.data.reduce<ImagesGrouped>((acc, curr) => {
            if (!curr.docType) return acc;

            if (!acc[curr.docType]) {
                acc[curr.docType] = new Map();
            }

            const documentId = curr.docID ?? null;
            if (!acc[curr.docType].has(documentId)) {
                acc[curr.docType].set(documentId, []);
            }

            acc[curr.docType].get(documentId)!.push(curr);
            return acc;
        }, {});
    }, [orderImagesQuery.data]);

    return (
        <div className="card card-compact h-full bg-white overflow-y-auto">
            <div className="card-body pt-0! gap-0!">
                <div className="card-title mb-0! sticky top-0 bg-white text-sm pt-4 pb-1.5">
                    Documents
                </div>
                {orderNumber === null ? (
                    <div className="text-gray-500 text-center py-4 flex items-center justify-center h-full">
                        No order number selected
                    </div>
                ) : orderImagesQuery.isLoading ? (
                    <div className="text-gray-500 text-center py-4 space-x-2 flex items-center justify-center h-full">
                        <span>Loading</span>
                        <span className="loading loading-spinner loading-xs"></span>
                    </div>
                ) : orderImagesQuery.isError ? (
                    <div className="text-red-500 text-center py-4 space-x-1.5 flex items-center justify-center h-full">
                        <MdErrorOutline className="text-lg" />
                        <span className="font-medium">
                            Failed to fetch order documents
                        </span>
                    </div>
                ) : Object.keys(groupedImages).length === 0 ? (
                    <div className="text-gray-500 text-center py-4 flex items-center justify-center h-full">
                        No documents
                    </div>
                ) : (
                    <div className="space-y-2">
                        {Object.entries(groupedImages).map(
                            ([documentType, documentsMap]) => {
                                const firstDoc = Array.from(
                                    documentsMap.values(),
                                )[0]?.[0];

                                return (
                                    <div key={documentType}>
                                        <div className="bg-gray-50 border font-medium px-2 py-0.5 flex items-center justify-between">
                                            <span>{documentType}</span>

                                            {/* Show status icon only when document is required */}
                                            {firstDoc?.isRequired === 1 &&
                                                (firstDoc.reqMet === 1 ? (
                                                    <FaRegCheckCircle className="text-green-500" />
                                                ) : (
                                                    <span className="text-red-500 flex items-center space-x-0.5 text-xs">
                                                        <MdOutlineCancel />
                                                        <span>Missing</span>
                                                    </span>
                                                ))}
                                        </div>
                                        <ul className="border divide-y">
                                            {Array.from(
                                                documentsMap.entries(),
                                            ).map(([_, images]) => {
                                                const imageCount =
                                                    images.filter(
                                                        (image) =>
                                                            image.imageUrl !==
                                                            null,
                                                    ).length;

                                                return (
                                                    <li
                                                        key={images?.[0].docID}
                                                        className="flex justify-between px-2 py-0.5 cursor-pointer"
                                                        onClick={() =>
                                                            orderInvoicing.setSelectedImages(
                                                                images
                                                            )
                                                        }
                                                    >
                                                        <span>
                                                            Driver Code:{" "}
                                                            {images?.[0]
                                                                .driverCode ??
                                                                "N/A"}
                                                        </span>
                                                        <span>
                                                            {imageCount} pages
                                                        </span>
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    </div>
                                );
                            },
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
